import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentMeta, PresignUploadInput } from '@guabi/shared';
import { apiDelete, apiGet, apiPost } from '@/lib/api';

export const documentsQueryKey = (wedId: string) => ['documents', wedId] as const;

export function useDocuments(wedId: string) {
  return useQuery({
    queryKey: documentsQueryKey(wedId),
    queryFn: () => apiGet<{ items: DocumentMeta[] }>(`/weddings/${wedId}/documents`),
  });
}

interface Presigned {
  uploadUrl: string;
  docId: string;
  expiresInSeconds: number;
}

/**
 * Presign, PUT straight to S3, then ask the server to confirm it landed.
 *
 * The file never passes through the API — a 25MB contract through a Lambda would
 * be slow, expensive and capped by the gateway's payload limit. The confirm step
 * exists because the browser saying "done" proves nothing; the server asks S3.
 */
export function useUploadDocument(wedId: string) {
  const qc = useQueryClient();
  return useMutation<DocumentMeta, Error, { file: File; vendorId?: string; venueId?: string }>({
    mutationFn: async ({ file, vendorId, venueId }) => {
      const input: PresignUploadInput = {
        filename: file.name,
        contentType: file.type as PresignUploadInput['contentType'],
        sizeBytes: file.size,
        ...(vendorId ? { vendorId } : {}),
        ...(venueId ? { venueId } : {}),
      };
      const presigned = await apiPost<Presigned>(`/weddings/${wedId}/documents/presign`, input);

      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        // Must match what was signed, or S3 rejects the signature.
        headers: { 'Content-Type': file.type },
      });
      if (!put.ok) throw new Error('upload-failed');

      return apiPost<DocumentMeta>(`/weddings/${wedId}/documents/${presigned.docId}/complete`, {});
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentsQueryKey(wedId) });
    },
  });
}

export function useDeleteDocument(wedId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { docId: string }>({
    mutationFn: ({ docId }) => apiDelete(`/weddings/${wedId}/documents/${docId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentsQueryKey(wedId) });
    },
  });
}

/** Fetched on demand: the link is short-lived, so there is no point holding one. */
export async function openDocument(wedId: string, docId: string): Promise<void> {
  const { url } = await apiGet<{ url: string }>(`/weddings/${wedId}/documents/${docId}/url`);
  window.open(url, '_blank', 'noopener,noreferrer');
}
